<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useUISettings } from 'dashboard/composables/useUISettings';
const route = useRoute();

const { uiSettings, updateUISettings } = useUISettings();

const isConversationRoute = computed(() => {
  const CONVERSATION_ROUTES = [
    'inbox_conversation',
    'conversation_through_inbox',
    'conversations_through_label',
    'team_conversations_through_label',
    'conversations_through_folders',
    'conversation_through_mentions',
    'conversation_through_unattended',
    'conversation_through_participating',
    'inbox_view_conversation',
    // Chat interno tem composer no canto inferior direito — o FAB do Lumos
    // sobrepunha o botão de enviar. Esconde aqui também.
    'internal_chat',
    'internal_chat_detail',
  ];
  return CONVERSATION_ROUTES.includes(route.name);
});

const showCopilotLauncher = computed(() => {
  return !uiSettings.value.is_copilot_panel_open && !isConversationRoute.value;
});
// A BOLHA SAI DA FRENTE AO ROLAR (06/10/2026). No celular ela cobria o fim de
// cartões, selos e botões em toda tela (medido em 390px: "Vencida" da Agenda,
// "Enviar documento", o chevron das conversas). Como nos apps Material, ela
// desce para fora da tela quando a pessoa rola para baixo — está lendo — e
// volta assim que rola para cima ou troca de tela. Só no toque; no
// computador fica sempre.
const recolhida = ref(false);
const ultimaPosicao = new WeakMap();
const LIMIAR = 8;

const aoRolar = evento => {
  const alvo =
    evento.target === document ? document.scrollingElement : evento.target;
  if (!(alvo instanceof Element)) return;
  const agora = alvo.scrollTop;
  const antes = ultimaPosicao.get(alvo) ?? agora;
  ultimaPosicao.set(alvo, agora);
  if (agora <= 0) recolhida.value = false;
  else if (agora - antes > LIMIAR) recolhida.value = true;
  else if (antes - agora > LIMIAR) recolhida.value = false;
};

let toque = null;
onMounted(() => {
  toque = window.matchMedia('(hover: none) and (pointer: coarse)');
  if (!toque.matches) return;
  document.addEventListener('scroll', aoRolar, {
    capture: true,
    passive: true,
  });
});
onBeforeUnmount(() => {
  document.removeEventListener('scroll', aoRolar, { capture: true });
});
watch(
  () => route.fullPath,
  () => {
    recolhida.value = false;
  }
);

const toggleSidebar = () => {
  updateUISettings({
    is_copilot_panel_open: !uiSettings.value.is_copilot_panel_open,
    is_contact_sidebar_open: false,
  });
};
</script>

<template>
  <!-- advup PWA mobile (2026-07-27) — achado real do Victor em device
       físico: FAB flutuava POR CIMA da bottom nav, sobreposto de forma
       feia. bottom-20/bottom-24 eram valores fixos que não sabiam da
       altura REAL da nav (que cresce com env(safe-area-inset-bottom) —
       fix anterior desta mesma sessão). calc() soma a altura base da nav
       (4rem) + o inset real + a folga que cada rota já tinha (1rem/1.25rem)
       — em navegador sem notch o env() é 0, resultado idêntico a antes. -->
  <!-- advup — 07/09/2026, pedido do Victor: a bolha ficava em DUAS alturas
       diferentes conforme a rota. Seis rotas ("compactas": ficha do processo,
       ficha do cliente, Financeiro, obrigação, relatórios de processos e
       lista de Casos) subiam o FAB porque, em 2026-07-17, ele cobria o
       "Salvar" de abas com pouco conteúdo. O efeito colateral é que a bolha
       dançava de tela em tela: MEDIDO em 1280x720, Financeiro a 84px do
       rodapé e Agenda Jurídica a 16px — 68px de diferença, visível a olho nu
       ao trocar de página.
       Uma altura só, a da Agenda. Se o "Salvar" voltar a ficar embaixo da
       bolha em alguma dessas seis telas, o conserto é dar espaço no RODAPÉ
       daquela tela, não mover a bolha: posição de elemento fixo não pode
       depender de quem está na tela. -->
  <div
    v-if="showCopilotLauncher"
    class="lumos-lugar fixed ltr:right-4 rtl:left-4 z-50 bottom-[calc(4rem+env(safe-area-inset-bottom)+1rem)] sm:bottom-4"
    :class="{ 'lumos-lugar--recolhida': recolhida }"
  >
    <!-- A BOLHA DO LUMOS (marca, 08/10/2026 — Victor: "você fez o branding
         da nossa bolha do Lumos?"). Íris é a cor da IA no produto; a bolha
         ganha o acabamento do ícone do login: brilho de cima, sombra embaixo,
         halo na própria cor, anel de vidro. Chega com um estalo ao abrir a
         tela e, no cursor, sobe, gira um fio e acende uma estrela — tudo uma
         vez, nada respira sozinho. -->
    <button
      v-tooltip.left="$t('LUMOS_FAB.DICA')"
      type="button"
      class="lumos-bolha"
      :aria-label="$t('LUMOS_FAB.ABRIR')"
      @click="toggleSidebar"
    >
      <span class="lumos-bolha__corpo" aria-hidden="true">
        <i class="i-ph-sparkle-fill lumos-bolha__ico" />
        <i class="i-ph-star-four-fill lumos-bolha__estrela" />
      </span>
    </button>
  </div>
  <template v-else />
</template>

<style scoped>
/* Recolhida: desce para fora da tela (atrás da barra de baixo) e some. */
.lumos-lugar {
  transition:
    transform 0.28s cubic-bezier(0.22, 1, 0.36, 1),
    opacity 0.2s ease;
}

.lumos-lugar--recolhida {
  opacity: 0;
  transform: translateY(calc(100% + 6rem));
  pointer-events: none;
}

/* O anel de vidro em volta da bolha: um objeto só, não um botão dentro de
   uma bolota. */
.lumos-bolha {
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  padding: 0;
  border: 0;
  border-radius: 999px;
  background: rgb(var(--iris-9) / 0.14);
  box-shadow: inset 0 0 0 1px rgb(var(--iris-9) / 0.18);
  backdrop-filter: blur(8px);
  cursor: pointer;
  animation: lumos-chega 0.55s cubic-bezier(0.34, 1.12, 0.44, 1) 0.4s backwards;
  transition:
    background-color 0.2s cubic-bezier(0.16, 1, 0.3, 1),
    transform 0.25s cubic-bezier(0.34, 1.12, 0.44, 1);
}

.lumos-bolha:hover {
  background: rgb(var(--iris-9) / 0.22);
  transform: translateY(-2px);
}

.lumos-bolha:active {
  transform: scale(0.95);
}

.lumos-bolha:focus-visible {
  outline: 2px solid rgb(var(--iris-9));
  outline-offset: 3px;
}

/* A bolha: o degradê da IA com o acabamento do ícone da marca. */
.lumos-bolha__corpo {
  position: relative;
  display: grid;
  place-items: center;
  width: 52px;
  height: 52px;
  border-radius: 999px;
  background: linear-gradient(
    150deg,
    rgb(var(--iris-8)) 0%,
    rgb(var(--iris-9)) 45%,
    rgb(var(--violet-10)) 100%
  );
  box-shadow:
    inset 0 1.5px 0 rgb(255 255 255 / 40%),
    inset 0 -4px 8px rgb(30 27 75 / 30%),
    0 0 0 1px rgb(255 255 255 / 16%),
    0 10px 24px -8px rgb(var(--iris-9) / 85%),
    0 0 28px -6px rgb(var(--violet-9) / 55%);
  transition:
    transform 0.3s cubic-bezier(0.34, 1.12, 0.44, 1),
    box-shadow 0.25s cubic-bezier(0.16, 1, 0.3, 1);
}

.lumos-bolha:hover .lumos-bolha__corpo {
  transform: rotate(-8deg);
  box-shadow:
    inset 0 1.5px 0 rgb(255 255 255 / 45%),
    inset 0 -4px 8px rgb(30 27 75 / 30%),
    0 0 0 1px rgb(255 255 255 / 20%),
    0 14px 30px -8px rgb(var(--iris-9) / 95%),
    0 0 36px -4px rgb(var(--violet-9) / 70%);
}

.lumos-bolha__ico {
  width: 24px;
  height: 24px;
  color: #fff;
  filter: drop-shadow(0 1px 2px rgb(30 27 75 / 35%));
}

/* A estrelinha que acende no cursor, no canto de cima. */
.lumos-bolha__estrela {
  position: absolute;
  top: 9px;
  right: 9px;
  width: 9px;
  height: 9px;
  color: #fff;
  opacity: 0;
  transform: scale(0.3) rotate(-30deg);
  transition:
    opacity 0.2s cubic-bezier(0.16, 1, 0.3, 1),
    transform 0.35s cubic-bezier(0.34, 1.12, 0.44, 1);
}

.lumos-bolha:hover .lumos-bolha__estrela {
  opacity: 1;
  transform: scale(1) rotate(0);
}

@keyframes lumos-chega {
  from {
    opacity: 0;
    transform: scale(0.5) rotate(-20deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .lumos-lugar,
  .lumos-bolha,
  .lumos-bolha:hover,
  .lumos-bolha:active,
  .lumos-bolha__corpo,
  .lumos-bolha:hover .lumos-bolha__corpo,
  .lumos-bolha__estrela {
    animation: none;
    transform: none;
    transition: none;
  }
}
</style>
